import * as THREE from "three";
import EventDispatcher from "../common/EventDispatcher";
import { Global } from "../common/global";
import gsap, { Linear } from "gsap";

export default class Defender {
  constructor(scene) {
    this.scene = scene;
    this.onEvents = this.onEvents.bind(this);
  }

  init(defenderKey) {
    this.events = EventDispatcher.getObj();
    this.events.addEventListener("GAMESCENE_EVENTS", this.onEvents);
    this.events.addEventListener("PLAYER_EVENTS", this.onEvents);
    this.events.addEventListener("GAME_EVENTS", this.onEvents);

    this.mixer = null;
    this.isCovering = false;
    this.defenderKey = defenderKey;
  }

  onEvents(data) {
    switch (data["message"]["event_type"]) {
      case "create_scene":
        this.addDefender();
        break;
      case "update":
        this.update(data["message"]["data"]);
        break;
      case "assign_position":
        this.faceToBall(data["message"]["data"]);
        break;
      case "defend":
        this.defend(data["message"]["data"]);
        break;
      case "goal":
        this.stopCover();
        break;
      case "dispose":
        this.dispose();
        break;
    }
  }

  placeBetween(keeperPosition, shooterPosition) {
    const midpoint = new THREE.Vector3();

    this.shooterPosition = shooterPosition;

    midpoint.addVectors(keeperPosition, shooterPosition).multiplyScalar(0.5);

    // Calculate the direction from the keeper to the shooter
    const direction = new THREE.Vector3()
      .subVectors(shooterPosition, keeperPosition)
      .normalize();

    // Calculate perpendicular direction (90 degrees to the left)
    const perpendicular = new THREE.Vector3(
      -direction.z,
      direction.y,
      direction.x
    );

    // Define offsets for left, center, and right positions
    const offset = 2; // Adjust this value to control the spacing between defenders

    let defenderPosition;

    switch (this.defenderKey) {
      case "defender1": // Left defender
        defenderPosition = new THREE.Vector3().addVectors(
          midpoint,
          perpendicular.multiplyScalar(-offset)
        );
        break;
      case "defender2": // Center defender
        defenderPosition = midpoint.clone();
        break;
      case "defender3": // Right defender
        defenderPosition = new THREE.Vector3().addVectors(
          midpoint,
          perpendicular.multiplyScalar(offset)
        );
        break;
      default:
        defenderPosition = midpoint.clone(); // Default to center if key is unknown
        break;
    }

    // Place the defender at the calculated position
    this.place(defenderPosition.x, defenderPosition.y, defenderPosition.z);

    // Ensure the defender faces the shooter
    this.defender.lookAt(shooterPosition);
  }

  place(x, y, z) {
    this.defPos = new THREE.Vector3(x, y, z);
    this.defender.visible = true;
    this.defender.position.set(x, y, z);
    this.defender.visible = false;
  }

  show() {
    this.defender.visible = true;
  }

  addDefender() {
    this.defender = Global.assets["glbs"][this.defenderKey]["asset"].scene;
    this.scene.add(this.defender);

    // Remove initial rotation
    // this.defender.rotation.y = Math.PI; // Commented out to avoid pre-rotation

    this.coverMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(0.6, 1.5),
      new THREE.MeshBasicMaterial({
        color: 0xff0000,
        transparent: true,
        opacity: 0,
      })
    );
    this.coverMesh.position.y = 0.7;
    this.defender.add(this.coverMesh);

    this.defender.visible = false;

    this.defender.userData = {
      canCheckIntersects: true,
      itemType: this.defenderKey,
    };

    this.defender.traverse((child) => {
      if (!child.material) return false;

      child.material.envMap = Global.envMap;
      child.material.envMapIntensity = 2;
      // child.material.opacity=1;
    });

    this.mixer = new THREE.AnimationMixer(this.defender);
    this.defenderIdle = THREE.AnimationClip.findByName(
      Global.assets["glbs"][`keeper`]["asset"]["animations"],
      "goal_keeper_idel"
    );
    this.idleAnimation = this.mixer.clipAction(this.defenderIdle);

    this.defenderDiveRight = THREE.AnimationClip.findByName(
      Global.assets["glbs"][`keeper`]["asset"]["animations"],
      "goal_keeper_ryt_save"
    );
    this.rightJumpAnimation = this.mixer.clipAction(this.defenderDiveRight);

    this.defenderDiveLeft = THREE.AnimationClip.findByName(
      Global.assets["glbs"][`keeper`]["asset"]["animations"],
      "goal_keeper_left_save"
    );
    this.leftJumpAnimation = this.mixer.clipAction(this.defenderDiveLeft);

    this.defenderDefend = THREE.AnimationClip.findByName(
      Global.assets["glbs"][`block`]["asset"]["animations"],
      "block."
    );
    this.defendAnimation = this.mixer.clipAction(this.defenderDefend);

    this.defenderSlideLeft = THREE.AnimationClip.findByName(
      Global.assets["glbs"][`keeper`]["asset"]["animations"],
      "side_step_left"
    );
    this.slideLeftAnimation = this.mixer.clipAction(this.defenderSlideLeft);

    this.defenderSlideRight = THREE.AnimationClip.findByName(
      Global.assets["glbs"][`keeper`]["asset"]["animations"],
      "side_step_ryt"
    );
    this.slideRightAnimation = this.mixer.clipAction(this.defenderSlideRight);

    this.defenderHappy = THREE.AnimationClip.findByName(
      Global.assets["glbs"][`keeper`]["asset"]["animations"],
      "celebration_2"
    );
    this.happyAnimation = this.mixer.clipAction(this.defenderHappy);

    this.defendAnimation.setLoop(THREE.LoopOnce, 1);
    this.defendAnimation.clampWhenFinished = true;

    this.happyAnimation.setLoop(THREE.LoopRepeat, 2);
    this.happyAnimation.clampWhenFinished = true;

    this.mixer.addEventListener("finished", (e) => {
      if (e.action === this.defendAnimation) {
        this.showHappy();
      }
      if (e.action === this.happyAnimation) {
        this.playIdle();
      }
    });
  }

  stopAll(discardKey) {
    discardKey !== "slide_left" &&
      discardKey !== "slide_right" &&
      this.coverTwn &&
      this.coverTwn.kill();
    discardKey !== "idle" && this.idleAnimation.stop();
    discardKey !== "right_jump" && this.rightJumpAnimation.stop();
    discardKey !== "defend" && this.defendAnimation.stop();
    discardKey !== "left_jump" && this.leftJumpAnimation.stop();
    discardKey !== "slide_left" && this.slideLeftAnimation.stop();
    discardKey !== "slide_right" && this.slideRightAnimation.stop();
    discardKey !== "happy" && this.happyAnimation.stop();
  }

  showHappy() {
    this.happyAnimation.play();
    this.stopAll("happy");
  }

  faceToBall(data) {
    this.idleAnimSpeed = Math.max(0.5, Math.random());
    this.playIdle();
    this.ballPosition = data.position;
    this.defender.lookAt(this.ballPosition);
  }

  stopCover() {
    this.coverTwn && this.coverTwn.kill();
    this.playIdle();
  }

  coverPost() {
    this.idleTO && clearTimeout(this.idleTO);

    const converVal = {
      progress: 0,
    };
    this.coverDir = 1;
    this.coverTwn = gsap.to(converVal, {
      progress: 0.5,
      duration: 0.75 + Math.random() * 0.3,
      yoyo: true,
      repeat: -1,
      delay: Math.random() * 1.5,
      ease: Linear.easeNone,
      onStart: function () {
        this.isCovering = true;
        this.slideRightAnimation.play();
        this.stopAll("slide_right");
      }.bind(this),
      onUpdate: function () {
        if (converVal.progress == 0 || converVal.progress == 1) {
          this.playIdle();
          this.stopAll("idle");
        }
        this.defender.lookAt(this.shooterPosition);
        this.defender.position.x = this.defPos.x + converVal.progress * 2;
      }.bind(this),
      onRepeat: function () {
        this.coverDir *= -1;

        if (this.coverDir == 1) {
          this.slideRightAnimation.play();
          this.stopAll("slide_right");
        } else {
          this.slideLeftAnimation.play();
          this.stopAll("slide_left");
        }
      }.bind(this),
      onReverseComplete: function () {},
    });
  }

  defend(data) {
    this.stopCover();
    if (data.itemType !== this.defenderKey) return false;

    this.events.dispatchEvent({
      type: "SOUND_EVENTS",
      message: {
        event_type: "play_sound",
        data: {
          sndKey: "catch",
        },
      },
    });
    this.isCovering = false;
    this.defendAnimation.play();
    this.stopAll("defend");
  }

  playIdle() {
    this.isCovering = false;
    this.idleAnimation.play();
    this.stopAll("idle");
  }

  update(data) {
    if (this.mixer)
      this.mixer.update(
        Global.delta * 0.013 * (this.isCovering ? 0.5 : 1 * this.idleAnimSpeed)
      );
  }

  dispose() {
    if (this.boundingBoxHelper) {
      this.scene.remove(this.boundingBoxHelper);
    }
    if (this.defender) {
      this.defender.traverse((child) => {
        if (child.isMesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((material) => material.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });
      this.scene.remove(this.defender);
      this.defender = null;
    }
    if (this.coverMesh) {
      this.coverMesh.geometry.dispose();
      this.coverMesh.material.dispose();
      this.scene.remove(this.coverMesh);
      this.coverMesh = null;
    }
    this.coverTwn && this.coverTwn.kill();

    this.events.removeEventListener("GAMESCENE_EVENTS", this.onEvents);
    this.events.removeEventListener("PLAYER_EVENTS", this.onEvents);
    this.events.removeEventListener("GAME_EVENTS", this.onEvents);
  }
}
